import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Eye, X, CheckCircle, XCircle, AlertCircle, ChevronLeft, ChevronRight } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminSubmissions() {
  const [submissions, setSubmissions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const limit = 20;

  useEffect(() => {
    fetchSubmissions();
  }, [page]);

  const fetchSubmissions = async () => {
    const token = localStorage.getItem("wiki_token");
    if (!token) return;

    setIsLoading(true);
    try {
      const response = await axios.get(
        `${API}/admin/submissions?token=${token}&limit=${limit}&skip=${page * limit}`
      );
      setSubmissions(response.data.submissions || []);
      setTotal(response.data.total || 0);
    } catch (error) {
      console.error("Error fetching submissions:", error);
      toast.error("Failed to load submissions");
    } finally {
      setIsLoading(false);
    }
  };

  const viewResults = async (submissionId) => {
    const token = localStorage.getItem("wiki_token");
    if (!token) return;

    try {
      const response = await axios.get(
        `${API}/admin/submission/${submissionId}?token=${token}`
      );
      setSelectedSubmission(response.data);
    } catch (error) {
      console.error("Error fetching submission:", error);
      toast.error("Failed to load submission details");
    }
  };

  const getRecommendationColor = (rec) => {
    if (rec === "LIKELY NOTABLE") return "text-green-600 bg-green-50";
    if (rec === "POSSIBLY NOTABLE") return "text-yellow-600 bg-yellow-50";
    return "text-red-600 bg-red-50";
  };

  const getStatusIcon = (status) => {
    if (status === "pass") return <CheckCircle className="w-4 h-4 text-green-600" />;
    if (status === "fail") return <XCircle className="w-4 h-4 text-red-600" />;
    return <AlertCircle className="w-4 h-4 text-yellow-600" />;
  };

  const totalPages = Math.ceil(total / limit);

  if (isLoading && submissions.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-[#72777d]">Loading submissions...</div>
      </div>
    );
  }

  return (
    <div>
      <h1 className="font-wiki text-2xl text-[#202122] mb-6">View Submissions</h1>
      
      <div className="bg-white border border-[#a2a9b1] rounded">
        {submissions.length === 0 ? (
          <div className="p-8 text-center text-[#72777d]">
            No submissions yet. Submissions will appear here after users check notability at /notability.
          </div>
        ) : (
          <>
            <table className="w-full">
              <thead className="bg-[#eaecf0] border-b border-[#a2a9b1]">
                <tr>
                  <th className="text-left px-4 py-3 text-sm font-medium text-[#202122]">Topic</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-[#202122]">Articles</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-[#202122]">Score</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-[#202122]">Result</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-[#202122]">Submitted</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-[#202122]">Actions</th>
                </tr>
              </thead>
              <tbody>
                {submissions.map((sub) => (
                  <tr key={sub.id} className="border-b border-[#eaecf0] hover:bg-[#f8f9fa]">
                    <td className="px-4 py-3 text-sm text-[#202122] font-medium">
                      {sub.topic}
                    </td>
                    <td className="px-4 py-3 text-sm text-[#72777d]">
                      {sub.articles_input?.length || 0} articles
                    </td>
                    <td className="px-4 py-3 text-sm text-[#202122]">
                      {sub.overall_score}%
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-xs px-2 py-1 rounded ${getRecommendationColor(sub.recommendation)}`}>
                        {sub.recommendation}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-[#72777d]">
                      {new Date(sub.submitted_at).toLocaleString()}
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => viewResults(sub.id)}
                        className="flex items-center gap-1 text-sm text-[#3366cc] hover:underline"
                      >
                        <Eye className="w-4 h-4" />
                        View Results
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            
            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-[#eaecf0]">
                <div className="text-sm text-[#72777d]">
                  Showing {page * limit + 1} - {Math.min((page + 1) * limit, total)} of {total}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setPage(p => Math.max(0, p - 1))}
                    disabled={page === 0}
                    className="p-1 hover:bg-[#eaecf0] rounded disabled:opacity-50"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  <span className="text-sm text-[#202122]">
                    Page {page + 1} of {totalPages}
                  </span>
                  <button
                    onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                    disabled={page >= totalPages - 1}
                    className="p-1 hover:bg-[#eaecf0] rounded disabled:opacity-50"
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Results Modal */}
      {selectedSubmission && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="p-4 border-b border-[#a2a9b1] flex items-center justify-between">
              <h2 className="font-wiki text-xl text-[#202122]">
                Results: {selectedSubmission.topic}
              </h2>
              <button
                onClick={() => setSelectedSubmission(null)}
                className="p-1 hover:bg-[#eaecf0] rounded"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-4 overflow-y-auto flex-1">
              {/* Summary */}
              <div className={`p-4 rounded mb-4 ${getRecommendationColor(selectedSubmission.recommendation)}`}>
                <div className="font-medium">{selectedSubmission.recommendation}</div>
                <div className="text-sm mt-1">Overall Score: {selectedSubmission.overall_score}%</div>
              </div>

              {/* Input Articles */}
              <h3 className="font-medium text-[#202122] mb-3">Submitted Articles</h3>
              <div className="space-y-3 mb-6">
                {selectedSubmission.articles_input?.map((article, idx) => (
                  <div key={idx} className="bg-[#f8f9fa] border border-[#a2a9b1] p-3 rounded">
                    <div className="text-sm font-medium text-[#202122]">Article {idx + 1}</div>
                    {article.url && (
                      <div className="text-sm text-[#3366cc] truncate">
                        <a href={article.url} target="_blank" rel="noopener noreferrer" className="hover:underline">
                          {article.url}
                        </a>
                      </div>
                    )}
                    {article.publication && (
                      <div className="text-sm text-[#72777d]">Publication: {article.publication}</div>
                    )}
                    {article.content && (
                      <div className="text-sm text-[#72777d] mt-2 line-clamp-3">
                        Content: {article.content.substring(0, 200)}...
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Analysis Results */}
              <h3 className="font-medium text-[#202122] mb-3">Analysis Results</h3>
              <div className="space-y-4">
                {selectedSubmission.results?.articles?.map((article, idx) => (
                  <div key={idx} className="border border-[#a2a9b1] rounded">
                    <div className="bg-[#eaecf0] px-4 py-2 border-b border-[#a2a9b1]">
                      <span className="font-medium">Article {article.index}</span>
                      {article.publication && (
                        <span className="text-[#72777d]"> — {article.publication}</span>
                      )}
                      <span className="float-right text-sm">
                        Score: {article.passes}/5
                      </span>
                    </div>
                    <div className="p-4 space-y-2">
                      {Object.entries(article.checks || {}).map(([key, check]) => (
                        <div key={key} className="flex items-start gap-2">
                          {getStatusIcon(check.status)}
                          <div className="flex-1">
                            <div className="text-sm font-medium text-[#202122]">
                              {key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                            </div>
                            <div className="text-sm text-[#72777d]">{check.explanation}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
